// Helpers shared between the slider-banner lazy-load action (productList store
// module) and its consumers (BaseProductGrid, TheGridBanner). A slider banner's
// `productSlider` starts as a lightweight payload ({id, title, imageUrl, ...})
// and is enriched in place with the full tile data on desktop; an enriched
// product is recognised by having `variationAttributes`.

/**
 * Stable key for a slider banner, derived from its product ids. Used both to
 * deduplicate in-flight fetches and as the `ids` query param of the request.
 * @param {Object} banner banner object (with a productSlider array)
 * @returns {string} comma-separated product ids
 */
export const getSliderBannerKey = (banner) =>
  (banner?.productSlider ?? [])
    .map((sliderProduct) => sliderProduct.id)
    .join(',');

/**
 * Whether a slider product already carries its full tile data.
 * @param {Object} product slider product (lightweight or enriched)
 * @returns {boolean} true if the product carries its full tile data
 */
export const isSliderProductEnriched = (product) =>
  !!product?.variationAttributes;

/**
 * Whether a slider banner's products have been enriched with full tile data.
 * @param {Object} banner banner object
 * @returns {boolean} true if the banner's products are enriched
 */
export const isSliderBannerEnriched = (banner) =>
  isSliderProductEnriched(banner?.productSlider?.[0]);

/**
 * Whether a slider banner's lazy-load resolved with no usable products (network
 * error or all products offline). Desktop hides such a banner instead of
 * showing permanent skeletons.
 * @param {Object} banner banner object
 * @returns {boolean} true if the lazy-load failed or returned no products
 */
export const isSliderBannerUnavailable = (banner) =>
  !!banner?.sliderProductsUnavailable;

/**
 * Build the minimal product object a single-image skeleton tile
 * (BaseProductTileImage) needs from a lightweight slider product.
 * @param {Object} sliderProduct lightweight slider product ({id, imageUrl, title})
 * @returns {Object} product shaped for the skeleton tile
 */
export const toSliderSkeletonProduct = (sliderProduct) => ({
  id: sliderProduct.id,
  image: { url: sliderProduct.imageUrl, alt: sliderProduct.title },
  sliderProductName: sliderProduct.title,
  productName: sliderProduct.title || '',
});
